/**
 * Resident Profile API — backend calls for the V2 resident profile page.
 *
 * READ (apiFetch):
 *   getresidentprofile             (resident basic info + groups + area_groups + ehr_data)
 *   getresidentdevices             (devices assigned to resident)
 *   getresidentwellness            (wellness config: check-in, rounding, ehs)
 *   getcareflowwindows             (careflow plans: types 1,4,5)
 *   getwellnesswindows             (check-in + rounds windows: types 1,2)
 *   sp_dev_getareasbycampusid      (areas for relocate/move-in pickers)
 *   sp_dev_getInovonicsDevices     (available devices for assign picker)
 *   getresidentlogs                (resident activity audit log)
 *   getresidentflag                (review flag data)
 *   core_preform_event_notes       (preformatted event note options)
 *
 * READ (apiReport):
 *   dashboardhourchart             (hourly alarm chart)
 *   dashboardweekchart             (weekly trending chart)
 *   eventReasonSummary             (event reason breakdown)
 *
 * READ (apiFetch):
 *   getlimitedevents               (recent events for resident)
 *
 * WRITE (apiTransaction):
 *   resident_edit_profile          (edit basic info)
 *   resident_photo_upload          (upload profile photo)
 *   device_assign_resident         (assign existing device)
 *   device_unassign_resident       (remove device)
 *   resident_move_profile          (relocate to new area)
 *   resident_disable_profile       (move out)
 *   resident_delete_profile        (permanent delete)
 *
 * WRITE (apiSave):
 *   core_event_notes PUT           (add event notes)
 *   core_resident POST             (move back in)
 */

import { apiFetch, apiTransaction, apiSave, apiReport } from '../../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ — Basic data (600s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch full resident profile by campus + resident ID. */
export function fetchResidentProfile(campusId, residentId) {
  return apiFetch({
    procedure: 'getresidentprofile',
    reqType: 'stored',
    payload: { resident_id: residentId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch emergency contacts by campus + resident ID. */
export function fetchEmergencyContacts(campusId, residentId) {
  return apiFetch({
    procedure: 'getemergencycontacts',
    reqType: 'stored',
    payload: { resident_id: residentId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch devices assigned to this resident. */
export function fetchResidentDevices(campusId, residentId) {
  return apiFetch({
    procedure: 'getresidentdevices',
    reqType: 'stored',
    payload: { resident_id: residentId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch resident wellness config (check-in, rounding, ehs). */
export function fetchResidentWellness(campusId, residentId) {
  return apiFetch({
    procedure: 'getresidentwellness',
    reqType: 'stored',
    payload: { resident_id: residentId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch careflow windows — types 1 (default), 4 (resident group), 5 (area group). */
export function fetchCareflowWindows(campusId) {
  return apiFetch({
    procedure: 'getcareflowwindows',
    reqType: 'stored',
    payload: {
      campus_id: campusId,
      care_types: '1,4,5',
      care_event_types: '1',
    },
    readOnly: true,
  });
}

/** Fetch wellness windows — types 1 (check-in), 2 (rounds). */
export function fetchWellnessWindows(campusId) {
  return apiFetch({
    procedure: 'getwellnesswindows',
    reqType: 'stored',
    payload: {
      campus_id: campusId,
      window_types: '1,2',
    },
    readOnly: true,
  });
}

/** Fetch all areas for the campus (relocate/move-in pickers). */
export function fetchAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch all available devices for assignment picker. */
export function fetchAvailableDevices(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getInovonicsDevices',
    reqType: 'stored',
    payload: {
      device_filter: '',
      device_category_filter: '1,2,3,4,5,6,7,8,9,10,11,12,15,16,17',
      campus_id: campusId,
    },
    readOnly: true,
  });
}

/** Fetch resident activity logs. */
export function fetchResidentLogs(residentId) {
  return apiFetch({
    procedure: 'getresidentlogs',
    reqType: 'stored',
    pKey: 'resident_id',
    pVal: residentId,
    readOnly: true,
  });
}

/** Fetch resident review flag data. */
export function fetchResidentFlag(campusId, residentId) {
  return apiFetch({
    procedure: 'getresidentflag',
    reqType: 'stored',
    payload: { campus_id: campusId, res_filter: `${residentId}` },
    readOnly: true,
  });
}

/** Fetch preformatted event note options. */
export function fetchPreformNotes(campusId) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_preform_event_notes',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// READ — Reporting data (1200s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch hourly alarm chart data for a resident. */
export function fetchHourChart(campusId, residentId) {
  return apiReport({
    repType: 'dashboardhourchart',
    campusId,
    filters: { residentFilter: residentId.toString() },
  });
}

/** Fetch weekly trending chart data for a resident. */
export function fetchWeekChart(campusId, residentId) {
  return apiReport({
    repType: 'dashboardweekchart',
    campusId,
    filters: { residentFilter: residentId.toString() },
  });
}

/** Fetch event reason summary for a resident within a date range. */
export function fetchReasonSummary(campusId, residentId, startDate, endDate) {
  return apiReport({
    repType: 'eventReasonSummary',
    campusId,
    startDate,
    endDate,
    filters: { residentFilter: residentId.toString() },
  });
}

/** Fetch limited recent events for a resident. */
export function fetchLimitedEvents(campusId, residentId) {
  return apiFetch({
    procedure: 'getlimitedevents',
    reqType: 'stored',
    payload: { campus_id: campusId, resident_id: residentId.toString() },
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE — Resident editing
// ---------------------------------------------------------------------------

/**
 * Edit resident basic info.
 * V1 TransType: resident_edit_profile
 */
export function editResidentProfile(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'resident_edit_profile',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Upload resident profile photo.
 * V1 TransType: resident_photo_upload
 */
export function uploadResidentPhoto(payload, session) {
  return apiTransaction({
    transType: 'resident_photo_upload',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Assign an existing device to a resident.
 * Builds payload/oldPayload from the raw device object (picker pattern).
 * V1 TransType: device_assign_resident
 */
export function assignDeviceToResident(device, residentId, session, residentAreaId) {
  const payload = [{
    device_id: device.device_id,
    campus_id: session.campusId,
    is_active: true,
    resident_id: parseInt(residentId),
    device_name: device.device_name,
    manufacture_model_id: parseInt(device.manufacture_model_id || 0),
    manufacture_id: device.manufacture_id,
    resident_area_id: residentAreaId || 0,
    device_use_id: parseInt(device.device_use_id || 0),
    descriptions: device.descriptions || '',
    is_resident: true,
    is_area_device: false,
    device_type_id: device.device_type_id,
    modified_by: session.userId,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    device_id: device.device_id,
    campus_id: device.campus_id,
    is_active: device.is_active,
    resident_id: device.resident_id,
    device_use_id: device.device_use_id,
    device_name: device.device_name,
    descriptions: device.descriptions,
    manufacture_model_id: device.manufacture_model_id,
    device_type_id: device.device_type_id,
  }];

  return apiTransaction({
    transType: 'device_assign_resident',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Remove (unassign) a device from a resident.
 * V1 TransType: device_unassign_resident
 */
export function removeDeviceFromResident(deviceId, residentId, session, deviceName = null) {
  const payload = [{
    device_id: deviceId,
    device_name: deviceName,
    is_active: false,
    resident_id: null,
    modified_by: session.userId,
    offsite: session.offsite,
    offsite_id: session.offsiteId,
  }];

  const oldPayload = [{
    device_id: deviceId,
    resident_id: residentId,
  }];

  return apiTransaction({
    transType: 'device_unassign_resident',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Relocate a resident to a different area.
 * V1 TransType: resident_move_profile
 */
export function relocateResident(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'resident_move_profile',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Move a resident out (mark inactive).
 * V1 TransType: resident_disable_profile
 */
export function moveOutResident(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'resident_disable_profile',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Move a resident back in (re-activate).
 * V1 uses apiSave POST on core_resident.
 */
export function moveInResident(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'resident_move_in_profile',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Permanently delete a resident.
 * V1 TransType: resident_delete_profile
 */
export function deleteResident(payload, session) {
  return apiTransaction({
    transType: 'resident_delete_profile',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Add an emergency contact for a resident.
 * V1 TransType: emergency_contact_add
 */
export function addEmergencyContact(payload, session) {
  return apiTransaction({
    transType: 'emergency_contact_add',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Edit an existing emergency contact.
 * V1 TransType: emergency_contact_edit
 */
export function editEmergencyContact(payload, oldPayload, session) {
  return apiTransaction({
    transType: 'emergency_contact_edit',
    payload,
    oldPayload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Delete an emergency contact.
 * V1 TransType: emergency_contact_delete
 */
export function deleteEmergencyContact(payload, session) {
  return apiTransaction({
    transType: 'emergency_contact_delete',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

/**
 * Add a note to an event.
 *
 * @param {Array}  noteItems — [{ event_id, campus_id, note, modified_by }]
 * @param {object} session   — { campusId, userId, clientId }
 */
export function addEventNote(noteItems, session) {
  return apiSave({
    table: 'core_event_notes',
    payload: noteItems,
    pKey: 'note_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'PUT',
  });
}
